import styled from '@emotion/styled/macro';
import Divider from '@mui/material/Divider';
import {
  Button, Checkbox, Dialog, DialogActions, DialogContent, FormControlLabel, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import React, {
  Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState,
} from 'react';
import { MdHelpOutline, MdOutlineClose } from 'react-icons/md';
import Grid from '@mui/material/Grid';
import { DataGrid } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import { toGroupMap } from 'common/util/generalUtils';
import { SimpleSearch } from 'common/components/SimpleSearch';
import { useCompanyFleets } from 'accountUsers/hooks/useCompanyFleets';
import { RoleType } from 'bluetail-globals';
import { AddAccountUserModalStepOneModel } from './AddAccountUserModalStepOne';
import { useAccountAircraft } from '../../../aircraft/hooks/useAccountAircraft';
import useAddAccountUserMutation, { AddAccountUserParams } from '../../hooks/useAddAccountUser';

const ModalHeader = styled.div`
  font-size: 20px;
  padding: 12px;
`;

const ModalBody = styled.div`
  display: flex;
  gap: 9px;
  flex-direction: column;
`;

const ModalDivider = styled(Divider)`
`;

const ModalHeaderActionContainer = styled(Stack)`
  justify-content: space-between;
`;

const CloseButton = styled(Button)`
  font-size: 20px;
  color: black;
`;

const SubmitButton = styled(Button)`
  width: 120px;
`;

const DataHeader = styled(Typography)`
  font-size: 14px;
`;

const DataCell = styled(Typography)`
  font-size: 12px;
`;

const StyledDialogContent = styled(DialogContent)`
  padding-top: 8px;
  min-height: 50vh;
`;

const StyledIconButton = styled(IconButton)`
  margin-top: -3px;
  margin-left: -6px;
`;

const StyledFleetStack = styled(Stack)`
  margin-bottom: 8px;
  margin-right: 8px;
  max-height: 60vh;
  overflow-y: auto;
`;

const StyledDataGrid = styled(DataGrid)`
  height: 50vh;
  margin-top: 8px;
`;

const StyledCheckbox = styled(Checkbox)`
  padding-top: 0;
  padding-bottom: 0;
`;

type AddAccountUserModalStepThreeFleetProps = {
    handleClose: () => void;
    goToPreviousStep: () => void;
    userInfo: AddAccountUserModalStepOneModel;
    companyId: number;
    refreshUserList: () => void;
};

type ViewProps = Omit<AddAccountUserModalStepThreeFleetProps, 'companyId' | 'refreshUserList'> & {
    handleSubmit: () => void;
    fleets: Omit<Fleet, 'aircrafts'>[];
    aircrafts: (Aircraft & { selected: boolean; })[];
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
    showOnlyAssignedAircraft: boolean;
    setShowOnlyAssignedAircraft: Dispatch<SetStateAction<boolean>>;
    allFleetsSelected: boolean;
    setAllFleetsSelected: Dispatch<SetStateAction<boolean>>;
    selectedFleets: Set<number>;
    setSelectedFleets: Dispatch<SetStateAction<Set<number>>>;
    numberOfSelectedAircraft: number;
    isAircraftSelected: (fleetId: number) => boolean;
};

function View({
  handleClose, handleSubmit, goToPreviousStep, userInfo, fleets, aircrafts, search, setSearch, showOnlyAssignedAircraft, setShowOnlyAssignedAircraft,
  allFleetsSelected, setAllFleetsSelected, selectedFleets, setSelectedFleets, numberOfSelectedAircraft, isAircraftSelected,
}: ViewProps): React.JSX.Element {
  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <ModalHeaderActionContainer spacing={2} direction="row">
        <ModalHeader>
          Select Fleets For User
          {' '}
          <em>
            {userInfo.firstName}
            {' '}
            {userInfo.lastName}
          </em>
        </ModalHeader>
        <CloseButton variant="text" onClick={handleClose}>
          <MdOutlineClose />
        </CloseButton>
      </ModalHeaderActionContainer>
      <ModalDivider />
      <StyledDialogContent>
        <ModalBody>
          <Grid container>
            <Grid item xs={3}>
              <Typography>
                Fleet:
                <Tooltip title={(
                  <Typography>
                    Custom fleets can be setup by clicking your name in the top right, selecting Account, then Fleets.
                    NOTE: If you navigate away from this screen you will need to start over.
                  </Typography>
                                )}
                >
                  <StyledIconButton>
                    <MdHelpOutline />
                  </StyledIconButton>
                </Tooltip>
              </Typography>
              <StyledFleetStack>
                <FormControlLabel
                  control={(
                    <StyledCheckbox
                      checked={allFleetsSelected}
                      onChange={(e) => setAllFleetsSelected(e.target.checked)}
                      disabled={fleets.length === 0}
                    />
                                    )}
                  label="All Aircraft (current and future)"
                />
                {fleets.map((f) => (
                  <FormControlLabel
                    key={f.fleet_id}
                    control={(
                      <StyledCheckbox
                        checked={selectedFleets.has(f.fleet_id)}
                        onChange={(e) => {
                          setAllFleetsSelected(false);
                          setSelectedFleets((p) => {
                            if (e.target.checked) {
                              p.add(f.fleet_id);
                            } else {
                              p.delete(f.fleet_id);
                            }

                            return new Set([...p.values()]);
                          });
                        }}
                      />
                    )}
                    label={f.fleet_name}
                  />
                ))}
              </StyledFleetStack>
            </Grid>
            <Grid item xs={9}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography>
                  User will receive access to
                  {' '}
                  {numberOfSelectedAircraft}
                  {' '}
                  total aircraft
                </Typography>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={showOnlyAssignedAircraft}
                      onChange={(e) => setShowOnlyAssignedAircraft(e.target.checked)}
                    />
                                    )}
                  label="Show Only Assigned Aircraft"
                />
              </Stack>
              <SimpleSearch search={search} setSearch={setSearch} placeHolder="Search Aircraft..." />
              <StyledDataGrid
                columns={[
                  {
                    renderHeader: () => (
                      <Checkbox
                        checked={allFleetsSelected}
                        disabled
                        indeterminate={!allFleetsSelected && selectedFleets.size > 0}
                      />
                    ),
                    field: 'selected',
                    renderCell: ({ row: { payment_id } }) => (
                      <Checkbox
                        checked={isAircraftSelected(payment_id as number)}
                        disabled
                      />
                    ),
                    sortable: false,
                  },
                  {
                    renderHeader: () => <DataHeader>Tail Number</DataHeader>,
                    field: 'tailnumber',
                    flex: 1,
                    renderCell: ({ row }) => (
                      <DataCell>
                        {row.reg_n_number}
                      </DataCell>
                    ),
                  },
                  {
                    renderHeader: () => <DataHeader>Serial Number</DataHeader>,
                    field: 'serial_number',
                    flex: 1,
                    renderCell: ({ row: { serial_number } }) => <DataCell>{serial_number}</DataCell>,
                  },
                  {
                    renderHeader: () => <DataHeader>Make</DataHeader>,
                    field: 'manufacturer',
                    flex: 1,
                    renderCell: ({ row: { manufacturer } }) => <DataCell>{manufacturer}</DataCell>,
                  },
                  {
                    renderHeader: () => <DataHeader>Model</DataHeader>,
                    field: 'model',
                    flex: 1,
                    renderCell: ({ row: { model } }) => <DataCell>{model}</DataCell>,
                  },
                ]}
                rows={aircrafts}
                disableRowSelectionOnClick
                getRowId={({ payment_id }) => payment_id as number}
                hideFooter
              />
            </Grid>
          </Grid>
        </ModalBody>
      </StyledDialogContent>
      <DialogActions>
        <Stack spacing={2} direction="row" justifyContent="end" width="100%">
          <Button onClick={goToPreviousStep}>Back</Button>
          <SubmitButton variant="contained" color="primary" onClick={handleSubmit}>
            Finish
          </SubmitButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

function Model(props:AddAccountUserModalStepThreeFleetProps): React.JSX.Element {
  const { companyId, refreshUserList } = props;
  const { aircraft: aircrafts } = useAccountAircraft({
    company_id: companyId,
    min: 0,
    max: 100_000_000,
    searchText: '',
  }) as { aircraft: Aircraft[] };
  const { rows: allFleetsWithDefault } = useCompanyFleets(companyId);
  const [search, setSearch] = useState('');
  const [showOnlyAssignedAircraft, setShowOnlyAssignedAircraft] = useState(false);
  const [allFleetsSelected, setAllFleetsSelected] = useState(false);
  const [selectedFleets, setSelectedFleets] = useState<Set<number>>(new Set());
  const addAccountUserMutation = useAddAccountUserMutation();

  // Note: 'All Aircraft' should be irrelevant now.  This was partof Janky fleet management, which has been phased out.
  const allFleets = useMemo(() => allFleetsWithDefault.filter((x) => x.fleet_name !== 'All Aircraft'), [allFleetsWithDefault]);

  const aircraftNumberToFleetIdMap = useMemo(
    () => toGroupMap(
      allFleets.flatMap((x) => x.enabled_aircrafts ?? []).filter(Boolean),
      (x) => x.aircraft_id,
      (x) => x.fleet_id,
    ),
    [allFleets],
  );

  const allAircrafts = useMemo<ViewProps['aircrafts']>(
    () => aircrafts
      .filter((x) => x.recurly_status === 1)
      .map((x) => ({
        ...x,
        selected: allFleetsSelected || (aircraftNumberToFleetIdMap.get(x.payment_id)?.some((y) => selectedFleets.has(y)) ?? false),
      })),
    [aircrafts, allFleetsSelected, selectedFleets, aircraftNumberToFleetIdMap],
  );

  const allAircraftsWithSelected = useMemo(
    () => allAircrafts
      .filter((x) => !showOnlyAssignedAircraft || x.selected),
    [allAircrafts, showOnlyAssignedAircraft],
  );

  const filteredAircrafts = useMemo(() => {
    const normalizedSearch = search.toLowerCase().split(' ');
    return allAircraftsWithSelected.filter((x) => normalizedSearch.every((s) => x.reg_n_number.toLowerCase().includes(s)
            || x.model.toLowerCase().includes(s)
            || x.serial_number.toLowerCase().includes(s)
            || x.manufacturer.toLowerCase().includes(s)));
  }, [search, allAircraftsWithSelected]);

  const numberOfSelectedAircraft = useMemo(
    () => allAircraftsWithSelected.filter((x) => x.selected).length,
    [allAircraftsWithSelected],
  );

  useEffect(() => {
    if (!allFleetsSelected) {
      return;
    }

    setSelectedFleets(new Set());
  }, [allFleetsSelected]);

  useEffect(() => {
    if (allFleets.length === 0) {
      setAllFleetsSelected(true);
    }
  }, [allFleets]);

  const isAircraftSelected = useCallback((paymentId: number) => {
    if (allFleetsSelected) {
      return true;
    }

    const matchedFleet = aircraftNumberToFleetIdMap.get(paymentId);
    if (!matchedFleet) {
      return false;
    }

    return matchedFleet.some((x) => selectedFleets.has(x));
  }, [allFleetsSelected, aircraftNumberToFleetIdMap, selectedFleets]);

  const handleSubmit = (): void => {
    const {
      handleClose,
      userInfo: {
        firstName, lastName, email, userType, role, loginNeverExpires, loginExpirationDate,
      },
    } = props;

    const params: AddAccountUserParams = {
      aircraft_assignment_method: 'all',
      fleets: null,
      aircrafts: null,
      company_id: companyId,
      first_name: firstName,
      last_name: lastName,
      email,
      login_expiry: loginNeverExpires ? null : loginExpirationDate.toISOString(),
      marketing_user_type: userType,
      role_id: role as RoleType,
    };

    if (!allFleetsSelected) {
      if (selectedFleets.size < 1) {
        toast.error('You must select at least one fleet');
        return;
      }

      params.aircraft_assignment_method = 'fleet';
      params.fleets = [...selectedFleets.values()];
    }

    addAccountUserMutation.mutate(params, {
      onSuccess: () => {
        toast.success(`User '${firstName} ${lastName}' created`);
        refreshUserList();
        handleClose();
      },
      onError: () => {
        toast.error('Error creating user');
      },
    });
  };

  const viewProps: ViewProps = {
    ...props,
    handleSubmit,
    fleets: allFleets,
    aircrafts: filteredAircrafts,
    search,
    setSearch,
    showOnlyAssignedAircraft,
    setShowOnlyAssignedAircraft,
    allFleetsSelected,
    setAllFleetsSelected,
    selectedFleets,
    setSelectedFleets,
    numberOfSelectedAircraft,
    isAircraftSelected,
  };

  return <View {...viewProps} />;
}

export default Model;
export { View };
